import { Component, OnInit, HostListener, OnDestroy } from "@angular/core";
import { MediaReplayService } from "./shared/utils/media-replay.service";
import { ExternalService } from "./shared/services/external.service";
import { NgxPermissionsService } from "ngx-permissions";
import { AppLoaderService } from "./shared/services/app-loader/app-loader.service";
import { EchoService } from "./shared/services/echo.service";
import { NotificationHandlerService } from "./shared/services/notification-handler.service";
import { Subscription } from "rxjs";

@Component({
  selector: "vr-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  private notificationSubscription: Subscription | null = null;

  constructor(
    public service: ExternalService,
    private echoService: EchoService,
    private notificationHandler: NotificationHandlerService
  ) {}

  ngOnInit() {
    // Initialize WebSocket notification system ONLY if user is already logged in
    // (e.g., page refresh after login)
    this.initializeNotifications();
  }

  /**
   * Public method to initialize WebSocket notifications
   * Called by LoginComponent after successful login
   */
  public startNotifications(): void {
    this.initializeNotifications();
  }

  /**
   * Initialize centralized notification system
   * Subscribes to user's notification channel and routes all notifications
   * through NotificationHandlerService
   */
  private initializeNotifications(): void {
    console.log('🚀 [App] Initializing WebSocket notification system...');

    // Get user ID from sessionStorage (stored by login.component.ts)
    const userDataString = sessionStorage.getItem('loggedInUser');

    if (!userDataString) {
      console.warn('⚠️ [App] User not authenticated (no "loggedInUser" in sessionStorage)');
      console.warn('⚠️ [App] Skipping WebSocket notification initialization');
      return;
    }

    console.log('✓ [App] User data found in sessionStorage');

    try {
      const userData = JSON.parse(userDataString);
      const userId = userData?.user?.id || userData?.id;

      if (!userId) {
        console.error('✗ [App] User ID not found in localStorage data structure');
        console.error('✗ [App] Expected: userData.user.id or userData.id');
        console.error('✗ [App] Actual userData structure:', userData);
        return;
      }

      console.log(`✓ [App] User ID extracted: ${userId}`);

      // Subscribe to user notification channel
      console.log('📡 [App] Subscribing to user notification channel...');

      this.notificationSubscription = this.echoService
        .listenToUserNotifications(userId)
        .subscribe(
          (notification) => {
            console.log('📨 [App] Notification received from Echo service:', notification);

            // Route notification through handler
            this.notificationHandler.handleNotification(notification);
          },
          (error) => {
            console.error('✗ [App] Error receiving notification:', error);
          }
        );

      console.log(`✓ [App] WebSocket notification system initialized for user ${userId}`);
      console.log('✅ [App] Ready to receive real-time notifications');
    } catch (error) {
      console.error('✗ [App] Error parsing user data from localStorage:', error);
      console.error('✗ [App] userDataString:', userDataString);
    }
  }

  ngOnDestroy(): void {
    // Clean up notification subscription
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    // Get user ID to stop listening
    const userDataString = sessionStorage.getItem('loggedInUser');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        const userId = userData?.user?.id || userData?.id;
        if (userId) {
          this.echoService.stopListeningToUserNotifications(userId);
        }
      } catch (error) {
        console.error('Error cleaning up notifications:', error);
      }
    }
  }
}
